export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LEVEL_ORDER: LogLevel[] = ['debug', 'info', 'warn', 'error'];

const CURRENT_STAGE =
  process.env.REACT_APP_STAGE ?? process.env.NODE_ENV ?? 'development';
console.log('current stage', CURRENT_STAGE);
const IS_DEV_STAGE = CURRENT_STAGE === 'development' || CURRENT_STAGE === 'dev';
console.log('is dev', IS_DEV_STAGE);
export interface LoggerOptions {
  level?: LogLevel;
  prefix?: string;
  transport?: (level: LogLevel, args: unknown[]) => void;
}

export class Logger {
  private levelIndex: number = LEVEL_ORDER.indexOf('info');
  private prefix?: string;
  private transport: (level: LogLevel, args: unknown[]) => void;

  constructor(options: LoggerOptions = {}) {
    const { level = 'info', prefix, transport } = options;
    this.setLevel(level);
    this.prefix = prefix;
    this.transport =
      transport ??
      ((logLevel, args) => {
        const method = logLevel === 'debug' ? 'debug' : logLevel;
        const emitter: ((...data: unknown[]) => void) | undefined = (
          console as unknown as Record<string, (...data: unknown[]) => void>
        )[method];
        const message = this.prefix ? [this.prefix, ...args] : args;
        if (emitter) {
          emitter(...message);
        } else {
          console.log(...message);
        }
      });
  }

  public setLevel(level: LogLevel): void {
    const nextIndex = LEVEL_ORDER.indexOf(level);
    if (nextIndex === -1) {
      throw new Error(`Unsupported log level: ${level}`);
    }
    this.levelIndex = nextIndex;
  }

  public setPrefix(prefix?: string): void {
    this.prefix = prefix;
  }

  public setTransport(
    transport: (level: LogLevel, args: unknown[]) => void
  ): void {
    this.transport = transport;
  }

  public debug(...args: unknown[]): void {
    this.emit('debug', args);
  }

  public info(...args: unknown[]): void {
    this.emit('info', args);
  }

  public warn(...args: unknown[]): void {
    this.emit('warn', args);
  }

  public error(...args: unknown[]): void {
    this.emit('error', args);
  }

  private emit(level: LogLevel, args: unknown[]): void {
    if (!this.shouldLog(level)) {
      return;
    }
    this.transport(level, args);
  }

  private shouldLog(level: LogLevel): boolean {
    if (level !== 'error' && !IS_DEV_STAGE) {
      return false;
    }
    return LEVEL_ORDER.indexOf(level) >= this.levelIndex;
  }
}

export const logger = new Logger();

export const createLogger = (options?: LoggerOptions): Logger =>
  new Logger(options);

export default logger;
